import React from "react";
import { useEffect, useState } from "react";
import "./FootageRestrictedLocateMaintenance.css";
import ExpandedMenu from "./ExpandedMenu";

import {
  getFootageRestrictedLocateConfigData,
  updateFootageRestrictedLocateConfigData,
  createFootageRestrictedLocateConfigData,
  getAllAvailableResponses,
  disableFootageRestrictedLocateConfigData,
  getAllUtilities,
  enableFootageRestrictedLocateConfigData,
} from "./api";

import {
  InputGroup,
  Form,
  Col,
  Row,
  Tooltip,
  OverlayTrigger,
  Button,
} from "react-bootstrap";
import FootageRestrictedLocateTable from "./FootageRestrictedLocateMaintenanceTable";
import snackBar from "src/utilities/snackBar/SnackBar";
import { ValidationError } from "yup";

export interface TableResults {
  FootageRestrictedLocateConfig: FootageRestrictedLocateConfig;
  UtilityName: string | null;
  Response: Response;
  NoBillResponse: Response;
  HourlyResponse: Response | null;
  NoBillHourlyResponse: Response | null;
}

export interface FootageRestrictedLocateConfig {
  Id: number;
  UtilityId: number;
  ResponseId: number;
  NoBillLocateID: number | null;
  NoPlantBillable: boolean | null;
  OverflowToHourly: boolean | null;
  HourlyResponseID: number | null;
  HourlyMinimum: number | null;
  MinBillableMinutes: number | null;
  MaxLocates: number | null;
  MaxFootage: number | null;
  FootagePerLocate: number;
  IsActive: boolean | null;
  DateDeactivated: String | null;
  DeactivatedBy: number | null;
  CreatedDate: String | null;
  CreatedBy: number | null;
  DateModified: String | null;
  ModifiedBy: number | null;
  DateDeleted: String | null;
  DeletedBy: number | null;
  Comments: string;
  NoBillHourlyResponseID: number | null;
  ContractId: number;
}

export interface Response {
  ResponseID: number;
  GUID: string;
  ResponseCode: string;
  ResponseText: string;
  ResponseUnitType: ResponseUnitType | null;
  ModifyDate: string;
  Inactive: boolean;
  DateDeactivated: null | string;
  IsSystem: boolean | null;
}

export enum ResponseUnitType {
  Ea = "EA",
  Hr = "HR",
}

export const defaultTableSelectedResults: TableResults = {
  FootageRestrictedLocateConfig: {
    Id: 0,
    UtilityId: 0,
    ResponseId: 0,
    NoBillLocateID: 0,
    NoPlantBillable: false,
    OverflowToHourly: false,
    HourlyResponseID: 0,
    HourlyMinimum: 0,
    MinBillableMinutes: 0,
    MaxLocates: 5,
    MaxFootage: 0,
    FootagePerLocate: 0,
    IsActive: false,
    DateDeactivated: "",
    DeactivatedBy: 0,
    CreatedDate: "",
    CreatedBy: 0,
    DateModified: "",
    ModifiedBy: 0,
    DateDeleted: "",
    DeletedBy: 0,
    Comments: "",
    NoBillHourlyResponseID: 0,
    ContractId: 0,
  },
  UtilityName: "",
  Response: {
    ResponseID: 0,
    GUID: "",
    ResponseCode: "",
    ResponseText: "",
    ResponseUnitType: ResponseUnitType.Ea,
    ModifyDate: "",
    Inactive: false,
    DateDeactivated: null,
    IsSystem: null,
  },
  NoBillResponse: {
    ResponseID: 0,
    GUID: "",
    ResponseCode: "",
    ResponseText: "",
    ResponseUnitType: ResponseUnitType.Ea,
    ModifyDate: "",
    Inactive: false,
    DateDeactivated: null,
    IsSystem: null,
  },
  HourlyResponse: null,
  NoBillHourlyResponse: null,
};

export interface Utility {
  UtilityID: number;
  UtilityName: string;
  UtilityAbbr: string;
  CustID: number;
  Inactive: boolean;
}

export const FootageRestrictedLocateMaintenance = () => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [tableData, setTableData] = useState<TableResults[]>([]);
  const [selectedTableData, setSelectedTableData] = useState<TableResults>(
    defaultTableSelectedResults
  );
  const [allTblResponses, setAllTblResponses] = useState<Response[]>();
  const [allUtiltities, setAllUtilities] = useState<Utility[]>();
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Handles data fetching from server
  const fetchData = async (
    fetchFunction: Function,
    setDataFunction: Function,
    errorMessage: string
  ) => {
    try {
      const data = await fetchFunction();
      if (data) {
        setDataFunction(data);
      }
    } catch (error) {
      console.error(errorMessage, error);
    }
  };

  // Handles popups on errors or success
  const handlePopup = (message: string, type: "success" | "error") => {
    snackBar({
      text: message,
      type,
      timeout: type === "success" ? 15000 : 30000,
    });

    if (type === "error") setLoadingStatus(false);
  };

  function handleRowClick(configId: number) {
    if (isEditMode) {
      handlePopup("Please save or cancel changes.", "error");
    }

    if (!isEditMode) {
      const data = tableData.find(
        (item) => item.FootageRestrictedLocateConfig.Id === configId
      );

      if (data) {
        setSelectedTableData(data);
        setMenuOpen(true);
      }
    }
  }

  function handleClosingExpandedMenu() {
    if (isEditMode) {
      handlePopup("Please save or cancel changes.", "error");
    }

    if (!isEditMode) {
      setMenuOpen(false);
    }
  }

  async function updateData(updatedConfig: FootageRestrictedLocateConfig) {
    try {
      const data = await updateFootageRestrictedLocateConfigData(updatedConfig);
      if (data === 200) {
        setIsEditMode(false);
        fetchData(
          getFootageRestrictedLocateConfigData,
          setTableData,
          "getFootageRestrictedLocateConfigData ~ error:"
        );
        handlePopup("Successfully Updated", "success");
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((error: ValidationError) => {
          handlePopup(error.message, "error");
        });
      } else {
        handlePopup(
          "There was a problem saving. Please try again later.",
          "error"
        );
        console.error("updateData ~ error:", error);
      }
    }
  }

  async function createData() {
    try {
      await createFootageRestrictedLocateConfigData().then((data) => {
        if (data) {
          fetchData(
            getFootageRestrictedLocateConfigData,
            setTableData,
            "getFootageRestrictedLocateConfigData ~ error:"
          );
          setSelectedTableData(data);
          setMenuOpen(true);
          setIsEditMode(true);
          handlePopup("Successfully Created", "success");
        }
      });
    } catch (error) {
      handlePopup(
        "There was a problem creating a new config. Please try again later.",
        "error"
      );
      console.error("createData ~ error:", error);
    }
  }

  async function disableConfig(Id: number) {
    try {
      const data = await disableFootageRestrictedLocateConfigData(Id);
      if (data === 200) {
        setIsEditMode(false);
        fetchData(
          getFootageRestrictedLocateConfigData,
          setTableData,
          "getFootageRestrictedLocateConfigData ~ error:"
        );
        setMenuOpen(false);
        handlePopup("Successfully Disabled", "success");
      }
    } catch (error) {
      handlePopup(
        "There was a problem disabling this row. Please try again later.",
        "error"
      );
      console.error("disableConfig ~ error:", error);
    }
  }

  async function enableConfig(Id: number) {
    try {
      const data = await enableFootageRestrictedLocateConfigData(Id);
      if (data === 200) {
        setIsEditMode(false);
        fetchData(
          getFootageRestrictedLocateConfigData,
          setTableData,
          "getFootageRestrictedLocateConfigData ~ error:"
        );
        handlePopup("Successfully Enabled", "success");
      }
    } catch (error) {
      handlePopup(
        "There was a problem enabling this row. Please try again later.",
        "error"
      );
      console.error("enableConfig ~ error:", error);
    }
  }

  // Fetch initial data
  useEffect(() => {
    setLoadingStatus(true);

    async function fetchInitialData() {
      await Promise.all([
        fetchData(
          getFootageRestrictedLocateConfigData,
          setTableData,
          "Error loading."
        ),
        fetchData(
          getAllAvailableResponses,
          setAllTblResponses,
          "getAllResponses ~ error:"
        ),
        fetchData(
          getAllUtilities,
          setAllUtilities,
          "loadAllUtilities ~ error:"
        ),
      ]);

      setLoadingStatus(false);
    }

    fetchInitialData();
  }, []);

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  // Used for searching and filtering inactive rows
  const filterData = (item: TableResults) => {
    // Filter inactive rows based on the showInactive state
    if (!showInactive && !item.FootageRestrictedLocateConfig.IsActive)
      return false;

    if (searchTerm === "") return true;

    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    if (item.UtilityName) {
      return (
        item.UtilityName.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.FootageRestrictedLocateConfig.ContractId.toString().includes(
          lowerCaseSearchTerm
        )
      );
    }
    return item.FootageRestrictedLocateConfig.ContractId.toString().includes(
      lowerCaseSearchTerm
    );
  };

  // Search results
  const filteredData = tableData?.filter(filterData);

  return (
    <div style={{ width: "100%", overflow: "hidden" }}>
      {/* Page Header */}
      <div style={{ margin: "10px" }}>
        <h3 style={{ fontWeight: "bold" }}>
          Footage Restricted Locate Maintenance
        </h3>
      </div>
      <div className="footage-res-locate-maintenance-page">
        {/* Table Controls */}
        <div className="footage-res-locate-tools">
          <Form>
            <Row>
              <Col>
                <InputGroup className="footage-res-locate-search-bar">
                  <InputGroup.Text id="searchBar" className="material-icons">
                    search
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </InputGroup>
              </Col>
              <Col style={{ justifyContent: "flex-end", display: "flex" }}>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowInactive(!showInactive)}
                  size="sm"
                >
                  {showInactive ? "Hide Inactive" : "Show Inactive"}
                </Button>
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="left"
                  overlay={<Tooltip id={`tooltip-left`}>Add Row.</Tooltip>}
                >
                  <span
                    className="material-icons"
                    style={{
                      color: "#28a745",
                      fontSize: "44px",
                      userSelect: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => createData()}
                  >
                    add
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>
          </Form>
        </div>
        {/* Start of Table */}
        <FootageRestrictedLocateTable
          loadingStatus={loadingStatus}
          filteredData={filteredData}
          selectedTableData={selectedTableData}
          handleRowClick={handleRowClick}
        />
      </div>

      {/* Side Menu */}
      <ExpandedMenu
        isOpen={menuOpen}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onClose={() => handleClosingExpandedMenu()}
        onSave={updateData}
        onDisable={disableConfig}
        onEnable={enableConfig}
        selectedTableData={selectedTableData}
        allTblResponses={allTblResponses}
        allUtilities={allUtiltities}
      />
      {/* Alerts */}
      <div id="snackbar">snacks</div>
    </div>
  );
};
